import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import Button from '../UI/Button';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: background-color var(--transition-normal), box-shadow var(--transition-normal);
  
  ${({ isScrolled }) => isScrolled && `
    background-color: white;
    box-shadow: var(--shadow-md);
  `}
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 15px var(--container-padding);
  max-width: var(--container-width);
  margin: 0 auto;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  position: relative;
`;

const Nav = styled.nav`
  display: flex;
  margin-left: 40px;
  margin-right: auto;
  
  @media (max-width: 992px) {
    display: none;
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  justify-content: center;
`;

const NavItem = styled.li`
  margin: 0 var(--spacing-md);
`;

const NavLink = styled.a`
  font-weight: 500;
  color: ${({ isScrolled }) => (isScrolled ? 'var(--color-text)' : 'white')};
  text-decoration: none;
  transition: color var(--transition-normal);
  position: relative;
  
  &:hover {
    color: var(--color-secondary);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background-color: var(--color-secondary);
    transition: width var(--transition-normal);
  }
  
  &:hover::after {
    width: 100%;
  }
`;

const BottomRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;


const Logo = styled.a`
  font-size: 0.9rem;
  font-weight: 700;
  color: ${({ isScrolled }) => (isScrolled ? 'var(--color-primary)' : 'white')};
  text-decoration: none;
  transition: color var(--transition-normal);
  
  span {
    color: var(--color-secondary);
  }
  
  &:hover {
    color: var(--color-secondary);
  }
`;

const PhoneContainer = styled.div`
  text-align: right;
  margin-left: auto;
  
  @media (max-width: 992px) {
    display: none;
  }
`;

const PhoneButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  padding: 3px 8px;
  
  svg {
    font-size: 0.7rem;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${({ isScrolled }) => (isScrolled ? 'var(--color-text)' : 'white')};
  cursor: pointer;
  padding: 8px;
  position: absolute;
  top: 5px;
  right: 0;
  z-index: 1002;
  
  @media (max-width: 992px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: white;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  padding: var(--spacing-lg);
  overflow-y: auto;
`;

const MobileMenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-xl);
  padding-bottom: var(--spacing-md);
  border-bottom: 1px solid var(--color-border);
  
  ${Logo} {
    font-size: 1rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--color-text);
  cursor: pointer;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color var(--transition-fast);
  
  &:hover {
    background-color: rgba(0,0,0,0.05);
  }
`;

const MobileNavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const MobileNavItem = styled.li`
  margin-bottom: var(--spacing-md);
`;

const MobileNavLink = styled.a`
  font-size: 1.2rem;
  font-weight: 500;
  color: var(--color-text);
  text-decoration: none;
  display: block;
  padding: var(--spacing-md) var(--spacing-sm);
  border-radius: var(--border-radius-md);
  transition: background-color var(--transition-fast);
  
  &:hover {
    color: var(--color-primary);
    background-color: rgba(30, 136, 229, 0.05);
  }
`;

const MobilePhoneButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: var(--spacing-xl);
  width: 100%;
  padding: 16px;
  font-size: 1.1rem;
  
  svg {
    font-size: 1.2rem;
  }
`;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Отслеживаем скролл для изменения стиля хедера
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Блокируем скролл при открытом мобильном меню
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);
  
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };
  
  const scrollToSection = (id, isMobile = false) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      if (isMobile) {
        closeMobileMenu();
      }
    }
  };
  
  return (
    <HeaderContainer isScrolled={isScrolled}>
      <HeaderContent>
        <TopRow>
          <Logo isScrolled={isScrolled} href="#home">
            Купить-<span>Стрейч.РФ</span>
          </Logo>
          
          <Nav>
            <NavList>
              <NavItem>
                <NavLink 
                  isScrolled={isScrolled} 
                  href="#home"
                  onClick={(e) => { e.preventDefault(); scrollToSection('home'); }}
                >
                  Главная
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink 
                  isScrolled={isScrolled} 
                  href="#catalog"
                  onClick={(e) => { e.preventDefault(); scrollToSection('catalog'); }}
                >
                  Каталог
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink 
                  isScrolled={isScrolled} 
                  href="#calculator"
                  onClick={(e) => { e.preventDefault(); scrollToSection('calculator'); }}
                >
                  Калькулятор Опта
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink 
                  isScrolled={isScrolled} 
                  href="#about"
                  onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}
                >
                  О нас
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink 
                  isScrolled={isScrolled} 
                  href="#partnership"
                  onClick={(e) => { e.preventDefault(); scrollToSection('partnership'); }}
                >
                  Партнерство
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink 
                  isScrolled={isScrolled} 
                  href="#reviews"
                  onClick={(e) => { e.preventDefault(); scrollToSection('reviews'); }}
                >
                  Отзывы
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink 
                  isScrolled={isScrolled} 
                  href="#contacts"
                  onClick={(e) => { e.preventDefault(); scrollToSection('contacts'); }}
                >
                  Контакты
                </NavLink>
              </NavItem>
            </NavList>
          </Nav>
          
          <PhoneContainer>
            <PhoneButton
              variant={'contained'}
              size={'small'}
              as="a" 
              href="tel:+74951339612"
            >
              <FontAwesomeIcon icon={faPhone} />
              <span>+7 (495) 133-96-12</span>
            </PhoneButton>
          </PhoneContainer>
        </TopRow>
        
        <BottomRow>
          <MobileMenuButton 
            isScrolled={isScrolled} 
            onClick={toggleMobileMenu}
            aria-label="Открыть меню"
          >
            <FontAwesomeIcon icon={faBars} />
          </MobileMenuButton>
        </BottomRow>
      </HeaderContent>
      
      <AnimatePresence>
        {isMobileMenuOpen && (
          <MobileMenu
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
          >
            <MobileMenuHeader>
              <Logo isScrolled={true} href="#home">
                Купить-<span>Стрейч.РФ</span>
              </Logo>
              <CloseButton onClick={closeMobileMenu} aria-label="Закрыть меню">
                <FontAwesomeIcon icon={faTimes} />
              </CloseButton>
            </MobileMenuHeader>
            
            <MobileNavList>
              <MobileNavItem>
                <MobileNavLink 
                  href="#home"
                  onClick={(e) => { e.preventDefault(); scrollToSection('home', true); }}
                >
                  Главная
                </MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink 
                  href="#catalog"
                  onClick={(e) => { e.preventDefault(); scrollToSection('catalog', true); }}
                >
                  Каталог
                </MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink 
                  href="#calculator"
                  onClick={(e) => { e.preventDefault(); scrollToSection('calculator', true); }}
                >
                  Калькулятор Опта
                </MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink 
                  href="#about"
                  onClick={(e) => { e.preventDefault(); scrollToSection('about', true); }}
                >
                  О нас
                </MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink 
                  href="#partnership"
                  onClick={(e) => { e.preventDefault(); scrollToSection('partnership', true); }}
                >
                  Партнерство
                </MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink 
                  href="#reviews"
                  onClick={(e) => { e.preventDefault(); scrollToSection('reviews', true); }}
                >
                  Отзывы
                </MobileNavLink>
              </MobileNavItem>
              <MobileNavItem>
                <MobileNavLink 
                  href="#contacts"
                  onClick={(e) => { e.preventDefault(); scrollToSection('contacts', true); }}
                >
                  Контакты
                </MobileNavLink>
              </MobileNavItem>
            </MobileNavList>
            
            <MobilePhoneButton
              variant={'contained'}
              size={'medium'}
              as="a"
              href="tel:+74951339612"
              fullWidth
            >
              <FontAwesomeIcon icon={faPhone} />
              +7 (495) 133-96-12
            </MobilePhoneButton>
          </MobileMenu>
        )}
      </AnimatePresence>
    </HeaderContainer>
  );
};

export default Header; 